import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TypeBadge } from '@/components/ui/type-badge';
import { SectorBadge } from '@/components/ui/sector-badge';
import { SpaBadge } from '@/components/ui/spa-badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Search, FileText, User, Loader2, MapPin, ArrowRight } from 'lucide-react';

interface Professional {
  id: string;
  headline: string | null;
  sectors: string[];
  open_to: string[];
}

type SectorType = 'mining' | 'processing' | 'infrastructure' | 'industrial' | 'services' | 'other';
type OppType = 'job' | 'tender' | 'jv' | 'training' | 'service_offer';

interface RecommendedOpp {
  id: string;
  title: string;
  type: OppType;
  sector: SectorType;
  country: string;
  city: string | null;
  spa_linked: boolean;
  published_at: string | null;
  company: { name: string };
}

export default function ProfessionalDashboard() {
  const { t } = useTranslation();
  const { user, profile, role, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [professional, setProfessional] = useState<Professional | null>(null);
  const [loading, setLoading] = useState(true);
  const [recommendations, setRecommendations] = useState<RecommendedOpp[]>([]);
  const [recsLoading, setRecsLoading] = useState(false);

  useEffect(() => {
    if (!authLoading && (!user || role !== 'professional')) {
      navigate('/auth');
      return;
    }

    if (user) {
      fetchProfessionalData();
    }
  }, [user, role, authLoading, navigate]);

  const fetchProfessionalData = async () => {
    if (!user) return;

    try {
      const { data } = await supabase
        .from('professionals')
        .select('id, headline, sectors, open_to')
        .eq('user_id', user.id)
        .single();

      if (data) {
        const prof = {
          id: data.id,
          headline: data.headline,
          sectors: (data.sectors as string[]) || [],
          open_to: (data.open_to as string[]) || [],
        };
        setProfessional(prof);
        fetchRecommendations(prof.sectors);
      }
    } catch (error) {
      console.error('Error fetching professional data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecommendations = async (sectors: string[]) => {
    if (sectors.length === 0) return;
    setRecsLoading(true);
    try {
      // Fetch all open opportunities with scoring data
      const { data: allOpportunities } = await supabase
        .from('opportunities')
        .select('id, title, type, sector, country, city, spa_linked, published_at, eligibility, company:companies(name)')
        .eq('status', 'open')
        .order('published_at', { ascending: false })
        .limit(50); // Fetch more to score and rank

      if (!allOpportunities) return;

      const professionalSectors = new Set(sectors);
      const professionalOpenTo = new Set((professional?.open_to as string[]) || []);
      const professionalCountry = profile?.country;
      const isDiaspora = profile?.is_diaspora;

      // Score each opportunity
      const scoredOpportunities = allOpportunities.map(opp => {
        let score = 0;

        // Sector alignment (30 points max)
        if (professionalSectors.has(opp.sector)) {
          score += 30;
        }

        // Type/preference alignment (25 points max)
        if (professionalOpenTo.has(opp.type)) {
          score += 25;
        }

        // Location preferences (20 points max)
        if (professionalCountry && opp.country === professionalCountry) {
          score += 20;
        } else if (professionalCountry && opp.country !== professionalCountry) {
          // Slight penalty for different country, but still consider
          score += 5;
        }

        // Diaspora eligibility bonus (15 points max)
        if (isDiaspora) {
          if (opp.eligibility === 'diaspora_welcome') {
            score += 15;
          } else if (opp.eligibility === 'international') {
            score += 10;
          }
        } else if (opp.eligibility === 'local') {
          score += 10;
        }

        // SPA-linked bonus (5 points)
        if (opp.spa_linked) {
          score += 5;
        }

        // Recency bonus (5 points max, decays over time)
        if (opp.published_at) {
          const daysSincePublished = (Date.now() - new Date(opp.published_at).getTime()) / (1000 * 60 * 60 * 24);
          if (daysSincePublished <= 7) {
            score += 5;
          } else if (daysSincePublished <= 30) {
            score += 3;
          } else if (daysSincePublished <= 60) {
            score += 1;
          }
        }

        return {
          ...opp,
          score
        };
      });

      // Sort by score descending, then by published date
      scoredOpportunities.sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        const da = a.published_at ? new Date(a.published_at).getTime() : 0;
        const db = b.published_at ? new Date(b.published_at).getTime() : 0;
        return db - da;
      });

      // Take top 5 recommendations
      setRecommendations(scoredOpportunities.slice(0, 5) as RecommendedOpp[]);
    } catch (e) {
      console.error(e);
    } finally {
      setRecsLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <Layout>
        <div className="container flex min-h-[50vh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  if (!professional) {
    return (
      <Layout>
        <div className="container py-12">
          <Card className="mx-auto max-w-lg">
            <CardHeader>
              <CardTitle>{t('professional.profile.title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="mb-4 text-muted-foreground">
                {t('professional.dashboard.setupPrompt')}
              </p>
              <Button asChild>
                <Link to="/profile/professional/setup">
                  {t('professional.dashboard.setupButton')}
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold">
            {t('professional.dashboard.welcome')}, {profile?.first_name}
          </h1>
          {professional.headline && (
            <p className="mt-1 text-muted-foreground">{professional.headline}</p>
          )}
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          <Card className="cursor-pointer transition-shadow hover:shadow-lg" onClick={() => navigate('/opportunities')}>
            <CardContent className="flex flex-col items-center p-6 text-center">
              <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
                <Search className="h-7 w-7 text-primary" />
              </div>
              <h3 className="font-semibold">{t('professional.dashboard.browseOpportunities')}</h3>
            </CardContent>
          </Card>

          <Card className="cursor-pointer transition-shadow hover:shadow-lg" onClick={() => navigate('/dashboard/professional/applications')}>
            <CardContent className="flex flex-col items-center p-6 text-center">
              <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-secondary/10">
                <FileText className="h-7 w-7 text-secondary" />
              </div>
              <h3 className="font-semibold">{t('professional.dashboard.myApplications')}</h3>
            </CardContent>
          </Card>

          <Card className="cursor-pointer transition-shadow hover:shadow-lg" onClick={() => navigate('/profile/professional/setup')}>
            <CardContent className="flex flex-col items-center p-6 text-center">
              <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-muted">
                <User className="h-7 w-7 text-muted-foreground" />
              </div>
              <h3 className="font-semibold">{t('professional.dashboard.editProfile')}</h3>
            </CardContent>
          </Card>
        </div>

        {/* Recommended Opportunities */}
        <div className="mt-8">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-xl font-bold">{t('professional.dashboard.recommended')}</h2>
            <Button variant="link" asChild>
              <Link to="/opportunities">{t('professional.dashboard.browseOpportunities')} <ArrowRight className="ml-1 h-4 w-4" /></Link>
            </Button>
          </div>

          {recsLoading ? (
            <div className="space-y-3">
              {[1,2,3].map(i => <Skeleton key={i} className="h-24 w-full" />)}
            </div>
          ) : recommendations.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center">
                <p className="text-muted-foreground">{t('professional.dashboard.noRecommendations')}</p>
                <Button variant="outline" className="mt-4" onClick={() => navigate('/opportunities')}>
                  {t('professional.dashboard.browseOpportunities')}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {recommendations.map(o => (
                <Card key={o.id} className="cursor-pointer transition-shadow hover:shadow-md" onClick={() => navigate(`/opportunities/${o.id}`)}>
                  <CardContent className="flex items-center justify-between p-4">
                    <div className="space-y-1">
                      <h3 className="font-semibold">{o.title}</h3>
                      <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                        <span>{o.company?.name}</span>
                        <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{o.city ? `${o.city}, ` : ''}{o.country}</span>
                      </div>
                      <div className="flex flex-wrap gap-2 mt-1">
                        <TypeBadge type={o.type} />
                        <SectorBadge sector={o.sector} />
                        {o.spa_linked && <SpaBadge size="sm" />}
                      </div>
                    </div>
                    <ArrowRight className="h-5 w-5 text-muted-foreground shrink-0" />
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
